
import _ from "lodash";
import { Trans } from 'react-i18next';
import translationKeys from '../translations/keys';
import { iterateOverResolutionsAndModes } from "./pricing";
import { getFieldValue } from "./maths";
import Span from "../components/span";

// Adapter ids and their capacities
const ADAPTERS = {
    'adapter8': 8,
    'adapter16': 16,
    'adapterrack32': 32,
    'adapterrack': 64
};

// Returns object with required number of each type of CA for given number of cloud cameras without enterprise if not specified
export const getRequiredCloudAdapters = (numberOfCloudCameras, noEnterprise = true) => {

    // Ensure adapters are in capacity order, smallest first
    // This will have shape [[id, capacity]]
    const smallAdapters = {...ADAPTERS};
    if (noEnterprise) {
        delete smallAdapters.adapterrack32;
        delete smallAdapters.adapterrack;
    }
    const adapters = Object.entries(smallAdapters).sort((a, b) => a[1] - b[1]);

    const required = {};

    let index = 0;
    for (const [id, capacity] of adapters) {
        if (index === 0) {
            // First calculate number of smallest adapter that would be needed
            required[capacity] = Math.ceil(numberOfCloudCameras / capacity);
        } else {
            // Then where possible replace multiple of each adapter with the next size up
            // e.g. replace every two 8s with a 16

            // Previous adapter id
            const lastId = adapters[index - 1][0];

            // How many of last adapter is the current adapter equivalent to
            const multiple = ADAPTERS[id] / ADAPTERS[lastId];

            // Calculate number of current adapter
            const numberRequired = Math.floor(required[ADAPTERS[lastId]] / multiple);

            if (numberRequired > 0) {
                required[capacity] = numberRequired;

                // Update value of previous adapter
                required[ADAPTERS[lastId]] %= multiple;

                // If we no longer require any of previous adapter, remove it
                if (required[ADAPTERS[lastId]] === 0) {
                    delete required[ADAPTERS[lastId]];
                }
            } else {
                // Cannot replace anymore adapters
                break;
            }
        }

        index++;
    }

    return required;
};

export const getSuggestedCloudAdapters = (amount) => {
    const sortedAdapters = _.orderBy(_.values(ADAPTERS), [], ['desc']);

    const result = _.reduce(sortedAdapters, (acc, adapterCapacity, adapterIdx) => {
        if (amount > 0) {
            if (adapterIdx === _.keys(sortedAdapters).length - 1 && amount <= adapterCapacity) {
                acc = {
                    ...acc,
                    [adapterCapacity]: 1
                }
            }
            if (amount >= adapterCapacity) {
                let q = amount/adapterCapacity;
                if (Number((q-Math.trunc(q)).toFixed(2)) === 0.5) {
                    q = Math.trunc(q);
                }
                acc = {
                    ...acc,
                    [adapterCapacity]: Math.round(q)
                };
            } else if (amount < adapterCapacity && amount > sortedAdapters[adapterIdx+1]) {
                acc = {
                    ...acc,
                    [adapterCapacity]: 1
                };
            }
            amount = amount - ((acc[adapterCapacity] * adapterCapacity) || 0);
            return acc;
        } else {
            return acc;
        }
    }, {});
    
    return result;
};

const getCloudAdaptersSentence = (cloudAdapters, accentColour) => {

    const getTypeName = key => key === 64 ? 'Enterprise 64' :  key === 32 ? 'Enterprise 32' : key;

    // Get sentence
    switch (Object.keys(cloudAdapters).length) {
        case 1:
        default: {
            return (
                <Trans
                    i18nKey={
                        translationKeys.registration.plan.plan_summary.cloud_adapters
                            .REQUIRED_CLOUD_ADAPTERS
                    }
                    count={Object.values(cloudAdapters)[0]}
                >
                    You'll need {{ count: Object.values(cloudAdapters)[0] }} ×
                    Cloud Adapter
                    <Span colour={accentColour}>
                        {{ type: getTypeName(Object.keys(cloudAdapters)[0]) }}
                    </Span>
                </Trans>
            );
        }
            
        case 2:
            return (
                <Trans
                    i18nKey={
                        translationKeys.registration.plan.plan_summary.cloud_adapters
                            .REQUIRED_CLOUD_ADAPTERS_TWO_TYPES
                    }
                >
                    You'll need {{ count1: Object.values(cloudAdapters)[0] }} ×
                    Cloud Adapter
                    <Span colour={accentColour}>
                        {{ type1: getTypeName(Object.keys(cloudAdapters)[0]) }}
                    </Span>
                    and {{ count2: Object.values(cloudAdapters)[1] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type2: getTypeName(Object.keys(cloudAdapters)[1]) }}
                    </Span>
                </Trans>
            );
        case 3:
            return (
                <Trans
                    i18nKey={
                        translationKeys.registration.plan.plan_summary.cloud_adapters
                            .REQUIRED_CLOUD_ADAPTERS_THREE_TYPES
                    }
                >
                    You'll need {{ count1: Object.values(cloudAdapters)[0] }} ×
                    Cloud Adapter
                    <Span colour={accentColour}>
                        {{ type1: getTypeName(Object.keys(cloudAdapters)[0]) }}
                    </Span>
                    , {{ count2: Object.values(cloudAdapters)[1] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type2: getTypeName(Object.keys(cloudAdapters)[1]) }}
                    </Span>
                    and {{ count3: Object.values(cloudAdapters)[2] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type3: getTypeName(Object.keys(cloudAdapters)[2]) }}
                    </Span>
                </Trans>
            );
        case 4:
            return (
                <Trans
                    i18nKey={
                        translationKeys.registration.plan.plan_summary.cloud_adapters
                            .REQUIRED_CLOUD_ADAPTERS_FOUR_TYPES
                    }
                >
                    You'll need {{ count1: Object.values(cloudAdapters)[0] }} ×
                    Cloud Adapter
                    <Span colour={accentColour}>
                        {{ type1: getTypeName(Object.keys(cloudAdapters)[0]) }}
                    </Span>
                    , {{ count2: Object.values(cloudAdapters)[1] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type2: getTypeName(Object.keys(cloudAdapters)[1]) }}
                    </Span>
                    , {{ count3: Object.values(cloudAdapters)[2] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type3: getTypeName(Object.keys(cloudAdapters)[2]) }}
                    </Span>
                    and {{ count4: Object.values(cloudAdapters)[3] }} × Cloud
                    Adapter
                    <Span colour={accentColour}>
                        {{ type4: getTypeName(Object.keys(cloudAdapters)[3]) }}
                    </Span>
                </Trans>
            );
    }
};

export const calculateNoOfCloudAdapters = (sites, optimised = false, accentColour) => {
    let cloudAdapters = {};

    if (sites && Object.keys(sites).length > 0) {
        for (let site of Object.values(sites)) {
            let camerasAtSite = 0;
            iterateOverResolutionsAndModes(
                (resolution, recordingMode) => 
                !_.isNil(site.fieldsProps) && Object.keys(site.fieldsProps).includes(`cameras${resolution}_${recordingMode}`) ?
                    camerasAtSite += parseInt(getFieldValue(site.fieldsProps[`cameras${resolution}_${recordingMode}`])) :
                    !_.isNil(site) && Object.keys(site).includes(`cameras${resolution}_${recordingMode}`) ? camerasAtSite += parseInt(getFieldValue(site[`cameras${resolution}_${recordingMode}`])) :
                    camerasAtSite += 0
            );
            let caAtSite = optimised ? getRequiredCloudAdapters(camerasAtSite) : getSuggestedCloudAdapters(camerasAtSite);
            const allKeys = new Set([...Object.keys(cloudAdapters), ...Object.keys(caAtSite)]);
            allKeys.forEach(key =>
                cloudAdapters[key] = (cloudAdapters[key] || 0) + (caAtSite[key] || 0)
            );
        }
    }

    // Can't be zero
    if (Object.keys(cloudAdapters).length === 0) {
        cloudAdapters[8] = 1;
    }

    const freeCloudAdapters = {
        types: cloudAdapters,
    };

    freeCloudAdapters.sentence = getCloudAdaptersSentence(cloudAdapters, accentColour);

    return freeCloudAdapters;
};

// TODO
// Adapter ids and their capacities
const ADAPTERS_QUOTE = {
    'adapter8': 8,
    'adapter16': 16,
    'adapterrack32': 32,
    'adapterrack': 'enterprise'
};
export const calculateNoOfCloudAdaptersFromQuote = (quote, accentColour) => {
    const cloudAdapters = quote.products.reduce((ca, { id, quantity }) => ({ ...ca, [ADAPTERS_QUOTE[id]]: quantity }), {});
    return {
        types: cloudAdapters,
        sentence: getCloudAdaptersSentence(cloudAdapters, accentColour)
    };
};

export const getCloudAdapterOrder = (cloudAdapters, caPricing) => {
    const products = {};
    for (const caType of Object.keys(cloudAdapters)) {
        if (cloudAdapters[caType] > 0) {
            products[caPricing[caType].id] = cloudAdapters[caType];
        }
    }
    return products;
};

